import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wand2, Globe, FileText, Loader2, AlertCircle, Plus, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminAPI } from '@/lib/admin-api-ext';
import { ConfidenceMeter } from '@/components/admin/ConfidenceMeter';
import { formatDateTime, formatDistanceToNow } from '@/lib/format-time';

const STATUS_BADGE = {
  queued: 'bg-muted text-muted-foreground border-border',
  running: 'bg-primary/10 text-primary border-primary/30',
  ready: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  applied: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  failed: 'bg-rose-500/10 text-rose-600 border-rose-500/30',
  needs_manual_review: 'bg-rose-500/10 text-rose-700 border-rose-500/40',
};

const NON_TERMINAL = new Set(['queued', 'running']);

export default function AdminIngestionJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [sourceType, setSourceType] = useState('all');

  // Initial load + filter changes
  useEffect(() => {
    setLoading(true);
    AdminAPI.ingest.list({
      status: status !== 'all' ? status : undefined,
      sourceType: sourceType !== 'all' ? sourceType : undefined,
      limit: 100,
    })
      .then(setJobs)
      .finally(() => setLoading(false));
  }, [status, sourceType]);

  // Wave B: auto-poll while at least one row is non-terminal. We only poll
  // the list (not each row) to keep the request count flat.
  useEffect(() => {
    const hasPending = jobs.some((j) => NON_TERMINAL.has(j.status));
    if (!hasPending) return;
    const t = setTimeout(async () => {
      try {
        const next = await AdminAPI.ingest.list({
          status: status !== 'all' ? status : undefined,
          sourceType: sourceType !== 'all' ? sourceType : undefined,
          limit: 100,
        });
        setJobs(next);
      } catch (_) { /* swallow; UI keeps the previous snapshot */ }
    }, 2500);
    return () => clearTimeout(t);
  }, [jobs, status, sourceType]);

  return (
    <div className="space-y-4" data-testid="admin-ingestion-jobs-page">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">AI Ingestion</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Ingestion jobs</h1>
          <p className="mt-1 text-xs text-muted-foreground">URL & PDF crawls with AI-extracted card metadata + rule drafts</p>
        </div>
        <Button asChild size="sm" className="gap-1.5" data-testid="admin-ingestion-jobs-new">
          <Link to="/admin/ingest"><Plus className="h-4 w-4" /> Ingest card</Link>
        </Button>
      </div>

      <Card className="p-3 flex gap-3 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="admin-ingestion-jobs-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="ready">Ready · awaiting review</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="needs_manual_review">Needs manual review</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceType} onValueChange={setSourceType}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="admin-ingestion-jobs-filter-source"><SelectValue placeholder="Source type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="url">URL</SelectItem>
            <SelectItem value="pdf">PDF</SelectItem>
          </SelectContent>
        </Select>
        {jobs.some((j) => NON_TERMINAL.has(j.status)) && (
          <div className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="admin-ingestion-jobs-live-indicator">
            <Loader2 className="h-3 w-3 animate-spin text-primary" /> Live · auto-refreshing
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <Table data-testid="admin-ingestion-jobs-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[34%]">Source</TableHead>
              <TableHead>Extracted</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Confidence</TableHead>
              <TableHead>Crawler</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="text-right">When</TableHead>
              <TableHead className="text-right w-[36px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center text-muted-foreground" data-testid="admin-ingestion-jobs-empty">
                  <Wand2 className="h-6 w-6 mx-auto opacity-50 mb-2" />
                  <div className="text-sm">No ingestion jobs yet.</div>
                  <Button asChild size="sm" className="mt-3"><Link to="/admin/ingest">Start your first ingest</Link></Button>
                </TableCell>
              </TableRow>
            ) : jobs.map((j) => {
              const cardName = j?.extracted?.card?.name?.value || j?.page_title || '—';
              const ruleCount = (j?.extracted?.rules || []).length;
              const milestoneCount = (j?.extracted?.milestones || []).length;
              const conf = j?.extracted?._meta?.overall_confidence ?? 0;
              return (
                <TableRow
                  key={j.id}
                  className="h-11 cursor-pointer"
                  data-testid={`admin-ingestion-jobs-row-${j.id}`}
                  onClick={() => navigate(`/admin/ingest/${j.id}`)}
                >
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {j.source_type === 'url' ? <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{cardName}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-md">{j.source_url || j.pdf_filename || '\u2014'}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    <span className="font-mono-num">{ruleCount}</span> rules · <span className="font-mono-num">{milestoneCount}</span> milestones
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant="outline" className={`text-[10px] gap-1 ${STATUS_BADGE[j.status] || ''}`}>
                      {(j.status === 'running' || j.status === 'queued') && <Loader2 className="h-3 w-3 animate-spin" />}
                      {(j.status === 'failed' || j.status === 'needs_manual_review') && <AlertCircle className="h-3 w-3" />}
                      {j.status}
                    </Badge>
                    {j.progress && NON_TERMINAL.has(j.status) && (
                      <div className="mt-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">{j.progress.replace(/_/g, ' ')}</div>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <ConfidenceMeter value={conf} />
                  </TableCell>
                  <TableCell className="py-2 text-[11px] text-muted-foreground">{j.crawler_used || '—'}</TableCell>
                  <TableCell className="py-2 text-[11px] text-muted-foreground">{j.created_by || '—'}</TableCell>
                  <TableCell className="py-2 text-right text-[11px] text-muted-foreground" title={formatDateTime(j.created_at)}>{formatDistanceToNow(j.created_at)}</TableCell>
                  <TableCell className="py-2 text-right">
                    <Link to={`/admin/ingest/${j.id}`} className="inline-block text-muted-foreground hover:text-foreground" data-testid={`admin-ingestion-jobs-open-${j.id}`}>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
